import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRight, Check, Shield, Zap, BarChart3, Target,
  Code, Users, Activity, Mail, Network, GitBranch,
  RefreshCw, Globe, Server, Lock, Sparkles, ChevronRight
} from 'lucide-react';
import logo from '../logo/logo.png';
import { apiService } from '../config/api';

/* ─────────────────── Animation Styles ─────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .sev-landing * { font-family: 'Inter', sans-serif; box-sizing: border-box; }

  @keyframes sev-drift1 {
    0%,100% { transform: translate(0,0) scale(1); }
    33% { transform: translate(60px,-40px) scale(1.1); }
    66% { transform: translate(-40px,30px) scale(0.95); }
  }
  @keyframes sev-drift2 {
    0%,100% { transform: translate(0,0) scale(1); }
    50% { transform: translate(-80px,50px) scale(1.15); }
  }
  @keyframes sev-drift3 {
    0%,100% { transform: translate(0,0) scale(1); }
    40% { transform: translate(50px,60px) scale(0.9); }
    80% { transform: translate(-30px,-50px) scale(1.1); }
  }
  @keyframes sev-fade-up {
    from { opacity:0; transform:translateY(28px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes sev-pulse-ring {
    0%   { box-shadow: 0 0 0 0 rgba(59,130,246,0.5); }
    70%  { box-shadow: 0 0 0 18px rgba(59,130,246,0); }
    100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
  }
  @keyframes sev-ping {
    0%   { transform:scale(1); opacity:1; }
    75%  { transform:scale(2.2); opacity:0; }
    100% { transform:scale(2.2); opacity:0; }
  }
  @keyframes sev-marquee {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @keyframes sev-rotate-slow {
    from { transform:rotate(0deg); }
    to   { transform:rotate(360deg); }
  }
  @keyframes sev-glow-pulse {
    0%,100% { opacity:.6; }
    50%      { opacity:1; }
  }
  @keyframes sev-float {
    0%,100% { transform:translateY(0); }
    50%      { transform:translateY(-10px); }
  }
  @keyframes sev-data-flow {
    0%   { stroke-dashoffset:400; opacity:0; }
    10%  { opacity:1; }
    90%  { opacity:1; }
    100% { stroke-dashoffset:0; opacity:0; }
  }

  .sev-fade-up      { animation: sev-fade-up .7s ease both; }
  .sev-d1           { animation-delay:.1s; }
  .sev-d2           { animation-delay:.25s; }
  .sev-d3           { animation-delay:.45s; }
  .sev-d4           { animation-delay:.65s; }
  .sev-d5           { animation-delay:.85s; }
  .sev-float        { animation: sev-float 4s ease-in-out infinite; }
  .sev-glow-pulse   { animation: sev-glow-pulse 2.5s ease-in-out infinite; }

  .sev-grid-bg {
    background-image:
      linear-gradient(rgba(59,130,246,.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59,130,246,.06) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  .sev-marquee-track {
    display:flex;
    width:max-content;
    animation: sev-marquee 28s linear infinite;
  }
  .sev-marquee-track:hover { animation-play-state:paused; }

  .sev-card-glow {
    transition: box-shadow .3s, transform .3s, border-color .3s;
  }
  .sev-card-glow:hover {
    transform:translateY(-6px);
  }

  .sev-btn-primary {
    position:relative; overflow:hidden;
    background: linear-gradient(135deg,#2563eb,#4f46e5);
    transition: transform .2s, box-shadow .2s;
  }
  .sev-btn-primary::before {
    content:''; position:absolute; inset:0;
    background:linear-gradient(135deg,#3b82f6,#6366f1);
    opacity:0; transition:opacity .3s;
  }
  .sev-btn-primary:hover { transform:translateY(-2px) scale(1.03); box-shadow:0 20px 50px -8px rgba(79,70,229,.5); }
  .sev-btn-primary:hover::before { opacity:1; }

  .sev-btn-ghost {
    background: rgba(255,255,255,.06);
    border: 1.5px solid rgba(255,255,255,.15);
    backdrop-filter:blur(8px);
    transition: border-color .25s, background .25s, transform .25s;
  }
  .sev-btn-ghost:hover {
    background:rgba(255,255,255,.1);
    border-color:rgba(99,102,241,.6);
    transform:translateY(-2px);
  }

  .sev-glass {
    background: rgba(255,255,255,.03);
    border: 1px solid rgba(255,255,255,.08);
    backdrop-filter: blur(16px);
  }

  .sev-node-line {
    stroke-dasharray: 400;
    stroke-dashoffset: 0;
  }
  .sev-node-flow {
    stroke-dasharray: 12 20;
    stroke-dashoffset: 400;
    animation: sev-data-flow 2.5s ease-in-out infinite;
  }

  .sev-stat-card {
    background:rgba(255,255,255,.04);
    border:1px solid rgba(255,255,255,.1);
    transition: background .3s, border-color .3s, transform .3s;
  }
  .sev-stat-card:hover {
    background:rgba(99,102,241,.12);
    border-color:rgba(99,102,241,.4);
    transform:translateY(-4px);
  }
`;

/* ─────────────────── Data ─────────────────── */
const FEATURES = [
  { icon: Mail, title: 'Bulk & Single Verify', desc: 'Upload full lists or check single emails — same enterprise accuracy either way.', color: '#3b82f6', glow: 'rgba(59,130,246,.3)' },
  { icon: Network, title: 'Multi-Node Distribution', desc: 'Parallel verification across unlimited satellite nodes for blazing throughput.', color: '#8b5cf6', glow: 'rgba(139,92,246,.3)' },
  { icon: GitBranch, title: 'Smart IP Failover', desc: 'IP block detected on one node? Emails automatically retry on the next available node.', color: '#f59e0b', glow: 'rgba(245,158,11,.3)' },
  { icon: RefreshCw, title: 'Auto Node Recovery', desc: 'Offline node mid-job? Work is detected and redistributed automatically, zero loss.', color: '#10b981', glow: 'rgba(16,185,129,.3)' },
  { icon: Globe, title: '30+ Provider Support', desc: 'Gmail, Outlook, Yahoo, iCloud, ProtonMail, Yandex, Zoho, GMX, and 20+ more.', color: '#06b6d4', glow: 'rgba(6,182,212,.3)' },
  { icon: Target, title: 'Smart Detection', desc: 'Detects disposable, role-based, catch-all, federated, and business accounts.', color: '#ec4899', glow: 'rgba(236,72,153,.3)' },
  { icon: BarChart3, title: 'Real-Time Dashboard', desc: 'Live job progress, per-node health, provider success rates and full result history.', color: '#a855f7', glow: 'rgba(168,85,247,.3)' },
  { icon: Activity, title: 'Provider Health Monitor', desc: 'Per-node, per-provider IP reputation tracking to spot blocks the moment they happen.', color: '#f97316', glow: 'rgba(249,115,22,.3)' },
  { icon: Shield, title: 'Enterprise Security', desc: 'API key auth, rate limiting, encrypted data, and role-based access control.', color: '#ef4444', glow: 'rgba(239,68,68,.3)' },
  { icon: Users, title: 'Lead Management', desc: 'Organize, tag, filter and export your verified leads in one clean interface.', color: '#14b8a6', glow: 'rgba(20,184,166,.3)' },
  { icon: Code, title: 'REST API & Webhooks', desc: 'Full API access to trigger verifications, pull results and hook into your own stack.', color: '#6366f1', glow: 'rgba(99,102,241,.3)' },
  { icon: Server, title: 'Admin Monitoring', desc: 'System health, job status, worker logs, and satellite node telemetry in one place.', color: '#64748b', glow: 'rgba(100,116,139,.3)' },
];

const PROVIDERS = [
  'Gmail', 'Outlook', 'Hotmail', 'Yahoo', 'AOL', 'iCloud', 'Zoho', 'ProtonMail',
  'Fastmail', 'Yandex', 'GMX', 'Mail.ru', 'Comcast', 'AT&T', 'Cox', 'Charter',
  'Tutanota', 'Rambler', 'WebDE', 'Laposte', 'Orange', 'EarthLink', 'Verizon',
];

const STATS = [
  { value: '98%', label: 'Accuracy Rate', note: 'Multi-layer deep verification' },
  { value: '10×', label: 'Faster With Nodes', note: 'Parallel satellite distribution' },
  { value: '30+', label: 'Providers Covered', note: 'Gmail, Outlook, Yahoo & more' },
  { value: '∞', label: 'Nodes Supported', note: 'Add as many as you need' },
];

/* ─────────────────── Node Network SVG ─────────────────── */
function NodeNetwork() {
  const nodes = [
    { label: 'Master', x: 200, y: 160, color: '#3b82f6', glow: '#3b82f6', size: 28, status: 'Primary' },
    { label: 'Node 1', x: 340, y: 80, color: '#10b981', glow: '#10b981', size: 20, status: 'Online' },
    { label: 'Node 2', x: 360, y: 180, color: '#10b981', glow: '#10b981', size: 20, status: 'Online' },
    { label: 'Node 3', x: 300, y: 265, color: '#10b981', glow: '#10b981', size: 20, status: 'Online' },
    { label: 'Node 4', x: 100, y: 265, color: '#8b5cf6', glow: '#8b5cf6', size: 20, status: 'Standby' },
    { label: 'Node 5', x: 55, y: 175, color: '#f59e0b', glow: '#f59e0b', size: 20, status: 'Blocked→' },
    { label: 'Node N', x: 90, y: 80, color: '#475569', glow: '#64748b', size: 20, status: 'Scale ∞' },
  ];
  const center = nodes[0];
  return (
    <svg viewBox="0 0 420 340" className="w-full h-full" style={{ maxHeight: 300 }}>
      <defs>
        {nodes.map((n, i) => (
          <filter key={i} id={`glow-${i}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        ))}
      </defs>

      {/* Connection lines */}
      {nodes.slice(1).map((n, i) => (
        <g key={i}>
          <line x1={center.x} y1={center.y} x2={n.x} y2={n.y}
            stroke={n.color} strokeWidth="1" strokeOpacity=".2" strokeDasharray="4 4" />
          <line x1={center.x} y1={center.y} x2={n.x} y2={n.y}
            stroke={n.color} strokeWidth="1.5" strokeOpacity=".7"
            strokeDasharray="12 20" strokeDashoffset="0"
            style={{ animation: `sev-data-flow ${2 + i * 0.4}s ease-in-out infinite`, animationDelay: `${i * 0.3}s` }}
          />
        </g>
      ))}

      {/* Nodes */}
      {nodes.map((n, i) => (
        <g key={i} style={{ cursor: 'default' }}>
          {/* Ping ring */}
          {n.status === 'Online' && (
            <circle cx={n.x} cy={n.y} r={n.size + 4}
              fill="none" stroke={n.color} strokeWidth="1.5" strokeOpacity=".5"
              style={{ animation: `sev-ping ${1.8 + i * 0.3}s ease-out infinite`, animationDelay: `${i * 0.5}s` }}
            />
          )}
          {/* Glow */}
          <circle cx={n.x} cy={n.y} r={n.size + 8}
            fill={n.glow} fillOpacity=".12"
            style={{ animation: `sev-glow-pulse 2s ease-in-out infinite`, animationDelay: `${i * 0.4}s` }}
          />
          {/* Node circle */}
          <circle cx={n.x} cy={n.y} r={n.size}
            fill="#0f172a" stroke={n.color} strokeWidth="2"
            filter={`url(#glow-${i})`}
          />
          {/* Label */}
          <text x={n.x} y={n.y - 2} textAnchor="middle" dominantBaseline="middle"
            fill={n.color} fontSize={i === 0 ? '8' : '6.5'} fontWeight="700">
            {n.label}
          </text>
          <text x={n.x} y={n.y + 8} textAnchor="middle" dominantBaseline="middle"
            fill={n.color} fontSize="5" opacity=".7">
            {n.status}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ─────────────────── Counter Hook ─────────────────── */
function useCounter(target, duration = 1600, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const num = parseFloat(target);
    if (isNaN(num)) { setVal(target); return; }
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(ease * num));
      if (progress < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return val;
}

/* ─────────────────── Feature Card ─────────────────── */
function FeatureCard({ feature, index }) {
  const [hovered, setHovered] = useState(false);
  const Icon = feature.icon;
  return (
    <div
      className="sev-card-glow rounded-2xl p-5 relative overflow-hidden"
      style={{
        background: hovered ? `rgba(${hexToRgb(feature.color)},.08)` : 'rgba(255,255,255,.025)',
        border: `1px solid ${hovered ? feature.color + '55' : 'rgba(255,255,255,.07)'}`,
        boxShadow: hovered ? `0 0 40px -8px ${feature.glow}` : 'none',
        animationDelay: `${index * 0.06}s`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Corner accent */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: hovered ? '100%' : 0, height: 2,
        background: `linear-gradient(90deg, ${feature.color}, transparent)`,
        transition: 'width .35s ease',
      }} />
      <div style={{
        display: 'inline-flex', padding: 10, borderRadius: 12, marginBottom: 12,
        background: `rgba(${hexToRgb(feature.color)},.15)`,
        border: `1px solid rgba(${hexToRgb(feature.color)},.25)`,
        color: feature.color,
        boxShadow: hovered ? `0 0 20px -4px ${feature.glow}` : 'none',
        transition: 'box-shadow .3s',
      }}>
        <Icon size={20} />
      </div>
      <h3 style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
        {feature.title}
      </h3>
      <p style={{ color: '#94a3b8', fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>
        {feature.desc}
      </p>
    </div>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

/* ─────────────────── Main Component ─────────────────── */
export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [seoConfig, setSeoConfig] = useState(null);

  useEffect(() => {
    // Fetch SEO metadata from backend
    const fetchSeo = async () => {
      try {
        const res = await apiService.getSeoMetadata();
        if (res.data?.data) {
          setSeoConfig(res.data.data);
          // Update Page Title and Meta tags
          document.title = res.data.data.title;

          // Inject meta tags if they don't exist
          const metaTags = [
            { name: 'description', content: res.data.data.description },
            { name: 'keywords', content: res.data.data.keywords },
            { name: 'author', content: res.data.data.author },
            { property: 'og:title', content: res.data.data.title },
            { property: 'og:description', content: res.data.data.description }
          ];

          metaTags.forEach(tag => {
            const attr = tag.name ? 'name' : 'property';
            const value = tag.name || tag.property;
            let el = document.querySelector(`meta[${attr}="${value}"]`);
            if (!el) {
              el = document.createElement('meta');
              el.setAttribute(attr, value);
              document.head.appendChild(el);
            }
            el.setAttribute('content', tag.content);
          });
        }
      } catch (err) {
        console.error('SEO loading failed', err);
      }
    };
    fetchSeo();
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setStatsVisible(true);
    }, { threshold: .3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="sev-landing" style={{ minHeight: '100vh', background: '#060d1a', color: '#e2e8f0', overflowX: 'hidden' }}>
      <style>{STYLES}</style>

      {/* ── Ambient background orbs ── */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '8%', left: '15%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,.12) 0%, transparent 70%)', animation: 'sev-drift1 18s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '40%', right: '5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,.1) 0%, transparent 70%)', animation: 'sev-drift2 22s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '15%', left: '30%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(6,182,212,.08) 0%, transparent 70%)', animation: 'sev-drift3 26s ease-in-out infinite' }} />
      </div>

      {/* ── Grid overlay ── */}
      <div className="sev-grid-bg" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: .4 }} />

      {/* ════════════════════════════════════════
                      HERO
         ════════════════════════════════════════ */}
      <section style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 60px' }}>
        <div style={{ maxWidth: 900, width: '100%', textAlign: 'center' }}>

          {/* Logo */}
          <div className="sev-fade-up sev-d1" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 28 }}>
            <img src={logo} alt="Smart Email Verifier" className="sev-float"
              style={{ display: 'block', height: 110, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 28px rgba(59,130,246,.6))' }} />
          </div>

          {/* Badge */}
          <div className="sev-fade-up sev-d2" style={{ marginBottom: 22, display: 'flex', justifyContent: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.35)', color: '#a5b4fc', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <Sparkles size={12} />
              Enterprise · Multi-Node · 30+ Providers · Auto-Failover
            </span>
          </div>

          {/* Headline */}
          <div className="sev-fade-up sev-d3" style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 'clamp(2.8rem, 7vw, 5.2rem)', fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.03em', background: 'linear-gradient(135deg, #f8fafc 0%, #93c5fd 40%, #818cf8 70%, #f8fafc 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>
              Smart Email<br />Verifier
            </h1>
          </div>

          {/* Sub */}
          <div className="sev-fade-up sev-d3" style={{ marginBottom: 36 }}>
            <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', color: '#94a3b8', lineHeight: 1.7, maxWidth: 620, margin: '0 auto', fontWeight: 400 }}>
              The most powerful email verification platform — distributed across unlimited nodes,
              with intelligent IP-block detection and automatic failover built right in.
            </p>
          </div>

          {/* Benefit pills */}
          <div className="sev-fade-up sev-d4" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginBottom: 44 }}>
            {['Protect sender reputation', 'Boost deliverability', 'Reduce bounce rates', 'Multi-node speed', 'Auto IP-block retry'].map((b, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 999, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', color: '#cbd5e1', fontSize: 12.5, fontWeight: 600 }}>
                <Check size={12} color="#22c55e" strokeWidth={3} />
                {b}
              </span>
            ))}
          </div>

          {/* CTA buttons */}
          {!isAuthenticated && (
            <div className="sev-fade-up sev-d5" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14 }}>
              <button className="sev-btn-primary" onClick={() => navigate('/login')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 32px', borderRadius: 14, color: 'white', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', position: 'relative', zIndex: 1 }}>
                Get Started Free
                <ArrowRight size={18} />
              </button>
              <button className="sev-btn-ghost" onClick={() => navigate('/login')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 32px', borderRadius: 14, color: '#cbd5e1', fontSize: 16, fontWeight: 600, cursor: 'pointer', border: '1.5px solid rgba(255,255,255,.15)' }}>
                Sign In
              </button>
            </div>
          )}

          {/* Scroll hint */}
          <div style={{ marginTop: 60, opacity: .35, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <ChevronRight size={18} style={{ transform: 'rotate(90deg)' }} />
            <span style={{ fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase' }}>Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
                   NODE ARCHITECTURE
         ════════════════════════════════════════ */}
      <section style={{ position: 'relative', zIndex: 1, padding: '80px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }}>

            {/* Left: text */}
            <div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', borderRadius: 999, background: 'rgba(139,92,246,.15)', border: '1px solid rgba(139,92,246,.3)', color: '#c4b5fd', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 20 }}>
                <Network size={12} />
                Satellite Node Architecture
              </span>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em', color: '#f1f5f9', margin: '0 0 16px' }}>
                Scale to Any Size.<br />
                <span style={{ background: 'linear-gradient(90deg,#818cf8,#38bdf8)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Automatically.
                </span>
              </h2>
              <p style={{ color: '#94a3b8', lineHeight: 1.75, fontSize: 15, marginBottom: 28 }}>
                Add unlimited satellite nodes — each with its own IP address — and the system
                distributes your verification jobs across all of them in parallel.
                If a provider silently blocks one node's IP, our N-node retry chain automatically
                routes those emails to the next available node. No human intervention needed.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { icon: Zap, text: 'All nodes work simultaneously in parallel for maximum throughput', color: '#f59e0b' },
                  { icon: Shield, text: 'IP block on Node 2? Emails retry on Node 3, 4… until resolved', color: '#10b981' },
                  { icon: RefreshCw, text: 'Node goes offline mid-job? Work auto-redistributes to healthy nodes', color: '#3b82f6' },
                  { icon: Server, text: 'Works perfectly with just the master server — no nodes required', color: '#8b5cf6' },
                ].map(({ icon: Icon, text, color }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `rgba(${hexToRgb(color)},.15)`, border: `1px solid rgba(${hexToRgb(color)},.3)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>
                      <Icon size={14} />
                    </div>
                    <p style={{ color: '#cbd5e1', fontSize: 13.5, lineHeight: 1.6, margin: 0, paddingTop: 6 }}>{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: network diagram */}
            <div style={{ background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 24, padding: 24, backdropFilter: 'blur(12px)' }}>
              <NodeNetwork />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 16 }}>
                {[
                  { dot: '#3b82f6', label: 'Master Server' },
                  { dot: '#10b981', label: 'Online Node' },
                  { dot: '#f59e0b', label: 'Auto-Retry' },
                  { dot: '#8b5cf6', label: 'Standby Node' },
                  { dot: '#475569', label: '+ Add More' },
                  { dot: '#22c55e', label: 'Auto-Failover' },
                ].map(({ dot, label }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: '#94a3b8', fontWeight: 600 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
                   PROVIDERS STRIP
         ════════════════════════════════════════ */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', borderBottom: '1px solid rgba(255,255,255,.06)', padding: '18px 0', background: 'rgba(255,255,255,.015)', overflow: 'hidden', position: 'relative', zIndex: 1 }}>
        <p style={{ textAlign: 'center', fontSize: 10, letterSpacing: '.15em', textTransform: 'uppercase', color: '#475569', fontWeight: 700, marginBottom: 14 }}>Verified support for 30+ major email providers</p>
        <div style={{ overflow: 'hidden' }}>
          <div className="sev-marquee-track">
            {[...PROVIDERS, ...PROVIDERS].map((p, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 16px', padding: '6px 18px', borderRadius: 999, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', color: '#94a3b8', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', opacity: .7, flexShrink: 0 }} />
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
                   FEATURES GRID
         ════════════════════════════════════════ */}
      <section style={{ position: 'relative', zIndex: 1, padding: '100px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 16px', borderRadius: 999, background: 'rgba(59,130,246,.12)', border: '1px solid rgba(59,130,246,.25)', color: '#93c5fd', fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 20 }}>
              <Sparkles size={12} /> All-in-One Platform
            </span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              Everything You Need
            </h2>
            <p style={{ color: '#64748b', fontSize: 16, maxWidth: 520, margin: '0 auto' }}>
              Validate, verify, and manage your email lists with confidence — at any scale
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {FEATURES.map((f, i) => <FeatureCard key={i} feature={f} index={i} />)}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
                      STATS
         ════════════════════════════════════════ */}
      <section ref={statsRef} style={{ position: 'relative', zIndex: 1, padding: '80px 24px', borderTop: '1px solid rgba(255,255,255,.05)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 20 }}>
            {STATS.map((s, i) => (
              <div key={i} className="sev-stat-card" style={{ padding: '28px 24px', borderRadius: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 'clamp(2rem,5vw,3rem)', fontWeight: 900, background: 'linear-gradient(135deg,#93c5fd,#818cf8)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1 }}>
                  {s.value}
                </div>
                <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, marginTop: 8 }}>{s.label}</div>
                <div style={{ color: '#475569', fontSize: 12, marginTop: 4 }}>{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
              HOW IT WORKS (Simple steps)
         ════════════════════════════════════════ */}
      <section style={{ position: 'relative', zIndex: 1, padding: '80px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.6rem,4vw,2.4rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.02em', marginBottom: 50 }}>
            Simple to Use. Powerful at Scale.
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 20 }}>
            {[
              { step: '01', title: 'Upload Your List', desc: 'CSV, Excel, or paste emails directly into the dashboard.', color: '#3b82f6' },
              { step: '02', title: 'System Verifies', desc: 'Multi-layer SMTP, MX, and provider checks run in parallel.', color: '#8b5cf6' },
              { step: '03', title: 'Smart Failover', desc: 'IP blocks auto-detected; emails retry on the next node.', color: '#f59e0b' },
              { step: '04', title: 'Download Results', desc: 'Clean, categorized export — Valid, Invalid, Unknown.', color: '#10b981' },
            ].map((s, i) => (
              <div key={i} style={{ padding: '28px 20px', borderRadius: 18, background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.07)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ fontSize: 48, fontWeight: 900, color: `rgba(${hexToRgb(s.color)},.1)`, lineHeight: 1, marginBottom: 12 }}>{s.step}</div>
                <div style={{ width: 36, height: 3, background: s.color, borderRadius: 999, marginBottom: 12 }} />
                <h3 style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ color: '#64748b', fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
                      CTA
         ════════════════════════════════════════ */}
      {!isAuthenticated && (
        <section style={{ position: 'relative', zIndex: 1, padding: '80px 24px' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <div style={{ position: 'relative', borderRadius: 28, overflow: 'hidden', padding: '64px 48px', textAlign: 'center', background: 'linear-gradient(135deg, rgba(37,99,235,.15) 0%, rgba(79,70,229,.15) 50%, rgba(6,182,212,.1) 100%)', border: '1px solid rgba(99,102,241,.3)' }}>
              {/* glow spots */}
              <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(99,102,241,.2)', filter: 'blur(60px)' }} />
              <div style={{ position: 'absolute', bottom: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(6,182,212,.15)', filter: 'blur(60px)' }} />
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 999, background: 'rgba(99,102,241,.2)', border: '1px solid rgba(99,102,241,.4)', color: '#a5b4fc', fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 22 }}>
                  <Lock size={11} /> Ready When You Are
                </div>
                <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.6rem)', fontWeight: 800, color: '#f8fafc', letterSpacing: '-0.02em', marginBottom: 16 }}>
                  Ready to Verify at Scale?
                </h2>
                <p style={{ color: '#94a3b8', fontSize: 15.5, lineHeight: 1.7, maxWidth: 520, margin: '0 auto 32px' }}>
                  Start with just the master server or go all-in with satellite nodes for
                  parallel, enterprise-grade speed. Your email lists deserve better.
                </p>
                <button className="sev-btn-primary" onClick={() => navigate('/login')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '15px 36px', borderRadius: 14, color: 'white', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', position: 'relative', zIndex: 1 }}>
                  Get Started Now
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════
                    FOOTER
         ════════════════════════════════════════ */}
      <footer style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,.06)', padding: '28px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logo} alt="SEV" style={{ height: 28, filter: 'brightness(.8)' }} />
            <span style={{ color: '#334155', fontSize: 12 }}>Smart Email Verifier</span>
          </div>

          {/* Promotion Link & SEO Backlink */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <a
              href="https://smartemailverifier.com"
              target="_blank"
              rel="noopener noreferrer"
              className="sev-hover-glow"
              style={{ padding: '6px 14px', borderRadius: 999, background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.2)', color: '#60a5fa', fontSize: 11, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.3s' }}
            >
              <Shield size={12} fill="currentColor" opacity={0.3} />
              Verified by SmartEmailVerifier.com
            </a>
          </div>

          <p style={{ color: '#334155', fontSize: 12, margin: 0 }}>
            © {new Date().getFullYear()} UnCutSolutions. All rights reserved. · Developed by{' '}
            <span style={{ color: '#4f46e5', fontWeight: 600 }}>AB | UnCutSolutions</span>
          </p>
        </div>
      </footer>
    </div>
  );
}